import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress } from '@mui/material'
import { capitalize } from '../../../utils/functions'
import { ui, tone } from '../../../utils/uiTokens'
import { AI_MENTOR_PAGE, AiMentorMessage, aiMentorHistoryApi } from '../../../features/curator/aiMentorHistory'

const KIND_LABEL: Record<string, string> = {
    CHAT: 'aimKindChat',
    EXPLAIN_TOPIC: 'aimKindTopic',
    EXPLAIN_TEST: 'aimKindTest',
    PLAN: 'aimKindPlan',
}

// yyyy-MM-ddTHH:mm:ss -> dd.MM HH:mm
const formatAt = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}`

interface Props {
    userCourseEnrollId: number
}

const Message: React.FC<{ m: AiMentorMessage }> = ({ m }) => {
    const { formatMessage, locale } = useIntl()
    const [open, setOpen] = useState(false)
    const ai = m.role === 'ASSISTANT'
    const topic = m[`topicTitle${capitalize(locale)}`] || m.topicTitleKz || m.topicTitleRu
    const long = ai && (m.text.length > 220 || m.text.split('\n').length > 3)

    return (
        <Box sx={styles.msg(ai)}>
            <Box sx={styles.meta}>
                <Box sx={{ color: ui.ink, fontWeight: 600, fontSize: '13px' }}>
                    {formatMessage({ id: ai ? 'aimRoleAi' : 'aimRoleStudent' })}
                </Box>
                <Box sx={{ color: ui.muted, fontSize: '12.5px' }}>{formatAt(m.createdAt)}</Box>
                <Box component="span" sx={styles.chip}>
                    {KIND_LABEL[m.kind] ? formatMessage({ id: KIND_LABEL[m.kind] }) : m.kind}
                </Box>
                {topic && <Box sx={{ color: ui.sub, fontSize: '12.5px', overflowWrap: 'anywhere' }}>{topic}</Box>}
            </Box>
            <Box
                sx={{
                    color: ui.ink,
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    ...(long && !open
                        ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                        : {}),
                }}
            >
                {m.text}
            </Box>
            {long && (
                <Box component="button" type="button" onClick={() => setOpen(!open)} sx={styles.link}>
                    {formatMessage({ id: open ? 'aimLess' : 'aimMore' })}
                </Box>
            )}
        </Box>
    )
}

// Оқушының AI ментормен диалогы (тек оқу). Область решает бэкенд (403 - блока нет).
const StudentAiMentorBlock: React.FC<Props> = ({ userCourseEnrollId }) => {
    const { formatMessage } = useIntl()
    const [items, setItems] = useState<AiMentorMessage[]>([])
    const [beforeId, setBeforeId] = useState<number | undefined>(undefined)
    const [hasMore, setHasMore] = useState(false)
    const { data, isFetching, isError } = aiMentorHistoryApi.useFetchAiMentorHistoryQuery(
        { userCourseEnrollId, beforeId },
        { skip: !userCourseEnrollId },
    )

    useEffect(() => {
        setItems([])
        setBeforeId(undefined)
        setHasMore(false)
    }, [userCourseEnrollId])

    useEffect(() => {
        if (!data) return
        setItems((prev) => {
            const seen = new Set(prev.map((x) => x.id))
            return [...prev, ...data.filter((x) => !seen.has(x.id))].sort((a, b) => a.id - b.id)
        })
        setHasMore(data.length >= AI_MENTOR_PAGE)
    }, [data])

    if (isError) return null

    const loadMore = () => items.length && setBeforeId(items[0].id)

    return (
        <Box sx={styles.card}>
            <Box sx={{ color: ui.ink, fontSize: '16px', fontWeight: 600 }}>{formatMessage({ id: 'aimTitle' })}</Box>
            {isFetching && <LinearProgress />}
            {hasMore && (
                <Box component="button" type="button" onClick={loadMore} disabled={isFetching} sx={styles.more}>
                    {formatMessage({ id: 'aimLoadMore' })}
                </Box>
            )}
            {!isFetching && data && !items.length && (
                <Box sx={{ color: ui.muted, fontSize: '14px' }}>{formatMessage({ id: 'aimEmpty' })}</Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map((m) => (
                    <Message key={m.id} m={m} />
                ))}
            </Box>
        </Box>
    )
}

export default StudentAiMentorBlock

const styles = {
    card: {
        gridColumn: '1 / -1',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        padding: '16px 20px',
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        minWidth: 0,
    },
    msg: (ai: boolean) => ({
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '4px',
        padding: '8px 12px',
        borderRadius: '10px',
        backgroundColor: ai ? ui.surface : ui.pinkSoft,
        minWidth: 0,
    }),
    meta: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '4px 10px',
    },
    chip: {
        display: 'inline-flex',
        padding: '2px 8px',
        borderRadius: '24px',
        fontSize: '12px',
        fontWeight: 600,
        color: tone.info.text,
        backgroundColor: tone.info.bg,
        border: `1px solid ${tone.info.border}`,
        whiteSpace: 'nowrap' as const,
    },
    link: {
        alignSelf: 'flex-start',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: '13px',
        padding: 0,
        background: 'transparent',
        border: 'none',
        color: ui.pink,
        '&:hover': { color: ui.pinkDark },
    },
    more: {
        alignSelf: 'flex-start',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: '13px',
        padding: '6px 12px',
        borderRadius: '12px',
        background: 'transparent',
        border: `1px solid ${ui.border}`,
        color: ui.sub,
        '&:hover': { borderColor: ui.pink, color: ui.pink },
    },
}
